import { type FC, useCallback, useMemo, useState } from 'react';
import { Button, Dropdown, Select, Switch, Tag } from 'antd';
import type { MenuProps } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import SearchBox from 'components/molecules/SearchBox';
import FilterIcon from 'assets/svg/Filter';
import NewGlobalFilter from 'components/molecules/common/NewGlobalFilter/NewGlobalFilter';
import FilterRibbon from 'components/molecules/PricingSimulator/FilterRibbon/FilterRibbon';
import { tpoGenUrls } from 'config/urls';
import { useCustomerConfig, useTpoGenConfig } from '../../context/CustomerConfigContext';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import type { AttributeOption, ReportView, SubMode, TimePeriod } from './types';

const renderTag = (props: any) => <Tag>{props.label}</Tag>;

export interface MetricToolbarProps {
    planId: string;
    reportView: ReportView;
    setReportView: (v: ReportView) => void;
    subMode: SubMode;
    setSubMode: (v: SubMode) => void;
    timePeriod: TimePeriod;
    setTimePeriod: (v: TimePeriod) => void;
    searchKeyword: string;
    setSearchKeyword: (v: string) => void;
    // Plan Metrics fields
    planDimension: string;
    setPlanDimension: (v: string) => void;
    planDimensionKpi: string;
    setPlanDimensionKpi: (v: string) => void;
    planMetricKpis: string[];
    setPlanMetricKpis: (v: string[]) => void;
    attributes: AttributeOption[];
    setAttributes: (v: AttributeOption[]) => void;
    attributeOptions: AttributeOption[];
    attributeSearch: string;
    setAttributeSearch: (v: string) => void;
    attributesLoading: boolean;
    onAttributeDropdownScroll?: () => void;
    // Promo Metrics fields
    promoDimensions: string[];
    setPromoDimensions: (v: string[]) => void;
    promoKpis: string[];
    setPromoKpis: (v: string[]) => void;
    onShowDeletePlan: () => void;
    onShowDeleteAllPromotions: () => void;
    showTrendChart: boolean;
    onToggleTrendChart: () => void;
}

const MetricToolbar: FC<MetricToolbarProps> = ({
    planId,
    reportView,
    setReportView,
    subMode,
    setSubMode,
    timePeriod,
    setTimePeriod,
    searchKeyword,
    setSearchKeyword,
    planDimension,
    setPlanDimension,
    planDimensionKpi,
    setPlanDimensionKpi,
    planMetricKpis,
    setPlanMetricKpis,
    attributes,
    setAttributes,
    attributeOptions,
    attributeSearch,
    setAttributeSearch,
    attributesLoading,
    onAttributeDropdownScroll,
    promoDimensions,
    setPromoDimensions,
    promoKpis,
    setPromoKpis,
    onShowDeletePlan,
    onShowDeleteAllPromotions,
    showTrendChart,
    onToggleTrendChart,
}) => {
    const { filterState } = usePlanDetailsContext();
    const { kpiCatalog } = useCustomerConfig();
    const tpoGenConfig = useTpoGenConfig();
    const { metricPageConfig } = tpoGenConfig;
    const basePlanActionMenuItems = tpoGenConfig.planActionMenuItems;
    const [search, setSearch] = useState<string>(searchKeyword);
    const KPI_OPTIONS = useMemo(() => kpiCatalog.map((k) => ({ label: k.name, value: k.key })), [kpiCatalog]);
    const PLAN_DIM_OPTIONS = useMemo(
        () => metricPageConfig.planMetricDimensions.map((d) => ({ label: d.label, value: d.value })),
        [metricPageConfig],
    );
    const PROMO_DIM_OPTIONS = useMemo(
        () => metricPageConfig.promoMetricDimensions.map((d) => ({ label: d.label, value: d.value })),
        [metricPageConfig],
    );
    const REPORT_VIEW_SELECT_OPTIONS = useMemo(
        () => metricPageConfig.reportViewOptions.map((r) => ({ label: r.label, value: r.value })),
        [metricPageConfig],
    );
    const TIME_PERIOD_SELECT_OPTIONS = useMemo(
        () => metricPageConfig.timePeriodOptions.map((t) => ({ label: t.label, value: t.value })),
        [metricPageConfig],
    );

    const isPlan = reportView === 'plan_metrics';
    const isPlanMetric = isPlan && subMode === 'metric';
    const isPlanDimension = isPlan && subMode === 'dimension';

    const handlePlanActionClick: MenuProps['onClick'] = useCallback(
        ({ key }: { key: string }) => {
            if (key === 'delete_plan') onShowDeletePlan();
            else if (key === 'delete_all_promotions') onShowDeleteAllPromotions();
            else if (key === 'view_trend_chart') onToggleTrendChart();
        },
        [onShowDeletePlan, onShowDeleteAllPromotions, onToggleTrendChart],
    );

    const planActionMenuItems = useMemo<MenuProps['items']>(() => {
        if (!basePlanActionMenuItems) return [];
        return basePlanActionMenuItems.map((item) => {
            if (!item) return item;
            if ('key' in item && item.key === 'view_trend_chart') {
                return {
                    ...item,
                    label: showTrendChart ? 'Close Trend Chart' : 'View Trend Chart',
                    disabled: !isPlanDimension,
                };
            }
            return { ...item, disabled: true };
        });
    }, [showTrendChart, isPlanDimension, basePlanActionMenuItems]);

    const handleSubModeChange = useCallback(
        (checked: boolean) => setSubMode(checked ? 'metric' : 'dimension'),
        [setSubMode],
    );

    const handlePlanDimensionChange = useCallback(
        (value: string) => {
            setPlanDimension(value);
            // Attribute domain changes when dimension changes -- clear attribute selections.
            setAttributes([]);
            setAttributeSearch('');
        },
        [setPlanDimension, setAttributes, setAttributeSearch],
    );

    // Multi-select clear handlers: empty selection resets to that field's config default.
    const handlePromoDimensionsChange = useCallback(
        (v: string[]) => {
            if (v.length === 0) setPromoDimensions([...metricPageConfig.defaultPromoMetricDimensions]);
            else setPromoDimensions(v);
        },
        [setPromoDimensions, metricPageConfig],
    );

    const handlePromoKpisChange = useCallback(
        (v: string[]) => {
            if (v.length === 0) setPromoKpis([...metricPageConfig.defaultKpis]);
            else setPromoKpis(v);
        },
        [setPromoKpis, metricPageConfig],
    );

    const handlePlanMetricKpisChange = useCallback(
        (v: string[]) => {
            if (v.length === 0) setPlanMetricKpis([...metricPageConfig.defaultKpis]);
            else setPlanMetricKpis(v);
        },
        [setPlanMetricKpis, metricPageConfig],
    );

    const attributeSelectOptions = useMemo(
        () =>
            attributeOptions.map((opt) => ({
                label: opt.attributeDesc,
                value: opt.attributeExId,
            })),
        [attributeOptions],
    );

    const selectedAttributeValue = attributes[0]?.attributeExId;

    const handleAttributesChange = useCallback(
        (value?: string) => {
            if (!value) {
                setAttributes([]);
                return;
            }
            const next =
                attributeOptions.find((o) => o.attributeExId === value) ??
                attributes.find((a) => a.attributeExId === value);
            setAttributes(next ? [next] : []);
        },
        [attributeOptions, attributes, setAttributes],
    );

    return (
        <>
            <div className="flex flex-row justify-between items-end gap-x-4 flex-wrap my-4">
                <div className="flex flex-row items-end gap-x-4">
                    {/* Dimension select */}
                    <div className="flex flex-col gap-y-1">
                        <p className="text-xs text-[#52525C]">{'Dimension'}</p>
                        {isPlan ? (
                            <Select
                                style={{ width: '10rem' }}
                                value={planDimension}
                                onChange={handlePlanDimensionChange}
                                options={PLAN_DIM_OPTIONS}
                            />
                        ) : (
                            <Select
                                mode="multiple"
                                allowClear
                                style={{ width: '14rem' }}
                                value={promoDimensions}
                                onChange={handlePromoDimensionsChange}
                                options={PROMO_DIM_OPTIONS}
                                tagRender={renderTag}
                                placeholder="Multiple Selection"
                                maxTagCount="responsive"
                            />
                        )}
                    </div>

                    {/* Attribute select (Plan + Metric only) */}
                    {isPlanMetric && (
                        <div className="flex flex-col gap-y-1">
                            <p className="text-xs text-[#52525C]">{'Attribute'}</p>
                            <Select
                                allowClear
                                style={{ width: '14rem' }}
                                value={selectedAttributeValue}
                                onChange={handleAttributesChange}
                                options={attributeSelectOptions}
                                showSearch
                                filterOption={false}
                                onSearch={setAttributeSearch}
                                searchValue={attributeSearch}
                                loading={attributesLoading}
                                onPopupScroll={(e) => {
                                    const target = e.currentTarget;
                                    if (
                                        target.scrollTop + target.offsetHeight >= target.scrollHeight - 32 &&
                                        onAttributeDropdownScroll
                                    ) {
                                        onAttributeDropdownScroll();
                                    }
                                }}
                                placeholder="Single Selection"
                            />
                        </div>
                    )}

                    {/* KPI select */}
                    <div className="flex flex-col gap-y-1">
                        <p className="text-xs text-[#52525C]">{'KPI'}</p>
                        {isPlan && subMode === 'dimension' ? (
                            <Select
                                style={{ width: '12rem' }}
                                value={planDimensionKpi}
                                onChange={(v: string) => setPlanDimensionKpi(v)}
                                options={KPI_OPTIONS}
                                showSearch
                                optionFilterProp="label"
                            />
                        ) : isPlan ? (
                            <Select
                                mode="multiple"
                                allowClear
                                style={{ width: '14rem' }}
                                value={planMetricKpis}
                                onChange={handlePlanMetricKpisChange}
                                options={KPI_OPTIONS}
                                showSearch
                                optionFilterProp="label"
                                tagRender={renderTag}
                                placeholder="Multiple Selection"
                                maxTagCount="responsive"
                            />
                        ) : (
                            <Select
                                mode="multiple"
                                allowClear
                                style={{ width: '14rem' }}
                                value={promoKpis}
                                onChange={handlePromoKpisChange}
                                options={KPI_OPTIONS}
                                showSearch
                                optionFilterProp="label"
                                tagRender={renderTag}
                                placeholder="Multiple Selection"
                                maxTagCount="responsive"
                            />
                        )}
                    </div>
                </div>

                <div className="flex flex-row justify-end items-end gap-x-3 flex-wrap">
                    <SearchBox
                        search={search}
                        setSearch={setSearch}
                        onSearch={() => setSearchKeyword(search)}
                        width={180}
                    />

                    {isPlan && (
                        <div className="flex flex-row items-center gap-x-2 pb-1">
                            <span
                                className={`text-xs ${subMode === 'dimension' ? 'text-[#18181B]' : 'text-[#A1A1AA]'}`}
                            >
                                {'Dimension'}
                            </span>
                            <Switch checked={subMode === 'metric'} onChange={handleSubModeChange} />
                            <span className={`text-xs ${subMode === 'metric' ? 'text-[#18181B]' : 'text-[#A1A1AA]'}`}>
                                {'Metric'}
                            </span>
                        </div>
                    )}

                    <div className="flex flex-col gap-y-1">
                        <p className="text-xs text-[#52525C]">{'Year'}</p>
                        <Select
                            style={{ width: '8rem' }}
                            value={timePeriod}
                            onChange={(v: TimePeriod) => setTimePeriod(v)}
                            options={TIME_PERIOD_SELECT_OPTIONS}
                        />
                    </div>

                    <div className="flex flex-col gap-y-1">
                        <p className="text-xs text-[#52525C]">{'View'}</p>
                        <Select
                            style={{ width: '10rem' }}
                            value={reportView}
                            onChange={(v: ReportView) => setReportView(v)}
                            options={REPORT_VIEW_SELECT_OPTIONS}
                        />
                    </div>

                    <div className="self-end pb-0">
                        <NewGlobalFilter
                            disabled={isPlan}
                            availableFilters={filterState.availableFilters}
                            allCheckedAttribute={filterState.allCheckedAttribute}
                            allSelectedValueForAllAttribute={filterState.allSelectedValueForAllAttribute}
                            applyFilterObject={filterState.applyFilterObject}
                            resetFilterObject={filterState.resetFilterObject}
                            onSubmitHandler={filterState.onFilterSaveClickHandler}
                            url={tpoGenUrls.getCalendarDataFilterAttributes(planId)}
                            selectedFilter={filterState.selectedFilter}
                            setSelectedFilter={filterState.setSelectedFilter}
                            tempApplyFilterObject={filterState.tempApplyFilterObject}
                            setTempApplyFilterObject={filterState.setTempApplyFilterObject}
                            apiVariables={filterState.filterApiVariables}
                            filterDimensionInfo={tpoGenConfig.calendarFilterDimensionInfo}
                            showAttributePanelToLeft
                            filterIcon={<FilterIcon />}
                        />
                    </div>

                    <Dropdown
                        menu={{ items: planActionMenuItems, onClick: handlePlanActionClick }}
                        trigger={['click']}
                        overlayStyle={{ width: '12rem', borderRadius: '0.5rem' }}
                    >
                        <Button>
                            <div className="flex flex-row justify-start items-center">
                                <EllipsisOutlined />
                            </div>
                        </Button>
                    </Dropdown>
                </div>
            </div>
            <div className="mb-3 mt-2">
                <FilterRibbon
                    allCheckedAttribute={filterState.allCheckedAttribute}
                    allSelectedValueForAllAttribute={filterState.allSelectedValueForAllAttribute}
                    applyFilterObject={filterState.applyFilterObject}
                    resetFilterObject={filterState.resetFilterObject}
                    onSubmitHandler={filterState.onFilterSaveClickHandler}
                />
            </div>
        </>
    );
};

MetricToolbar.displayName = 'MetricToolbar';
export default MetricToolbar;
